import { FC, Ref } from 'react';
import { classNames } from 'primereact/utils';
import { UploadIcon } from '@repo/atomic-components';

interface FileDropOverlayProps {
  /** Ref attached to the overlay element by `useFileDropZone`. */
  overlayContainerRef: Ref<HTMLDivElement>;
  /** Only renders the overlay while a drag is in progress. */
  isDragging: boolean;
  /** When non-null, displayed in red under the helper text — e.g. on
   *  invalid MIME-type drops. */
  error?: string | null;
  /** Headline shown while the user is dragging. */
  title?: string;
  /** Sub-line shown under the headline. */
  subtitle?: string;
}

/**
 * Full-viewport drop overlay rendered while the user is dragging files
 * over the window. Drop logic / drag-counter / file MIME-filtering all
 * live in `useFileDropZone` — this component is purely the visual
 * affordance.
 */
export const FileDropOverlay: FC<FileDropOverlayProps> = ({
  overlayContainerRef,
  isDragging,
  error,
  title = 'Drop documents to upload',
  subtitle = 'Release to add documents',
}) => {
  if (!isDragging) return null;
  return (
    <div ref={overlayContainerRef} className="fixed inset-0 z-[1200] flex items-center justify-center">
      <div
        className={classNames(
          'bg-fletchers-white/50 border-fletchers-gray-200 flex flex-col items-center justify-center',
          'rounded-lg border-2 border-dashed backdrop-blur-lg',
          'h-full w-full',
        )}
      >
        <UploadIcon className="text-fletchers-black-900 mb-6" style={{ width: '32px', height: '32px' }} />
        <p className="text-fletchers-black-900 mb-2 text-xl font-semibold">{title}</p>
        <p className="text-fletchers-gray-200 text-sm">{subtitle}</p>
        {error && <p className="text-fletchers-red-500 mt-4 text-sm">{error}</p>}
      </div>
    </div>
  );
};
